import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ButtonHTMLAttributes, DetailedHTMLProps, ReactNode } from "react";
import { forwardRef } from "react";
import { LoadingSpinner } from "../../feedback/LoadingSpinner";
import { ExternalLinkIcon } from "../../icons/ExternalLinkIcon";
import { classNames } from "../../utils/classNames";

type SvgIconProps = {
	className?: string;
	title?: string;
	titleId?: string;
};

type SvgIconComponent = (props: SvgIconProps) => ReactNode;

export const buttonStyles = cva(
	[
		"relative",
		"border",
		"rounded-lg disabled:cursor-not-allowed disabled:opacity-50",
		"inline-flex",
		"gap-2",
		"align-center",
		"items-center",
		"font-medium",
		"border",
	],
	{
		variants: {
			intent: {
				primary: "border-transparent bg-primary text-on-primary shadow-xs hover:bg-primary-strong",
				secondary: "border-neutral bg-white text-neutral shadow-xs enabled:hover:bg-neutral-faded",
				tertiary:
					"border-transparent bg-transparent text-neutral-faded underline hover:bg-neutral-faded hover:text-neutral",
				action: "border-transparent bg-transparent text-accent underline hover:bg-neutral-faded hover:text-accent-strong",
				flat: "rounded-none border-transparent bg-transparent text-neutral-faded shadow-xs enabled:hover:bg-button-flat-hover",
				nav: "border-transparent bg-primary-faded text-primary shadow-xs hover:bg-neutral-faded",
			},
			size: {
				xs: ["text-xs", "py-0.5", "px-1.5", "min-w-[40px]", "h-7"],
				sm: ["text-sm", "py-1", "px-2.5", "min-w-[60px]", "h-8"],
				md: ["text-sm", "py-1.5", "px-3", "min-w-[80px]", "h-9"],
				lg: ["text-base", "py-2", "px-4", "min-w-[100px]", "h-10"],
				custom: [],
			},
			align: {
				center: ["justify-center"],
				right: ["justify-end"],
				left: ["justify-start"],
			},
			isActive: {
				true: "",
				false: "",
			},
			isTight: {
				true: "gap-1 p-0",
				false: "",
			},
			isFlush: {
				true: "",
				false: "",
			},
		},
		compoundVariants: [
			{
				size: "xs",
				isFlush: true,
				className: "-ml-1.5",
			},
			{
				size: "sm",
				isFlush: true,
				className: "-ml-2.5",
			},
			{
				size: "md",
				isFlush: true,
				className: "-ml-3",
			},
			{
				size: "lg",
				isFlush: true,
				className: "-ml-4",
			},
			{
				intent: "flat",
				isActive: true,
				className: "bg-button-flat-hover",
			},
		],
		defaultVariants: {
			intent: "primary",
			size: "md",
			align: "center",
		},
	},
);

const buttonDestructiveVariants = cva([], {
	variants: {
		intent: {
			primary: "bg-critical text-on-critical hover:bg-[rgb(var(--os-color-critical-700))] focus:outline-critical",
			secondary: "text-critical hover:text-critical-hover",
			tertiary: "text-neutral-faded hover:text-neutral",
			flat: "",
			action: "",
			nav: "",
		},
	},
});

const loadingSpinnerStyles = cva([], {
	variants: {
		intent: {
			primary: "text-white",
			secondary: "text-primary",
			tertiary: "text-primary",
			flat: "text-primary",
			action: "text-accent",
			nav: "text-disabled",
		},
		isDestructive: {
			true: "",
			false: "",
		},
	},
	compoundVariants: [
		{ intent: "secondary", isDestructive: true, className: "text-critical" },
		{ intent: "tertiary", isDestructive: true, className: "text-neutral-faded" },
	],
});

type LinkProps =
	| {
			href?: never;
	  }
	| {
			href: string;
			isExternal?: boolean;
	  };

export type ButtonProps = Omit<
	DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
	"disabled"
> & {
	leftIcon?: SvgIconComponent;
	rightIcon?: SvgIconComponent;
	intent?: VariantProps<typeof buttonStyles>["intent"];
	size?: VariantProps<typeof buttonStyles>["size"];
	align?: VariantProps<typeof buttonStyles>["align"];
	isLoading?: boolean;
	isDestructive?: boolean;
	isActive?: boolean;
	isDisabled?: boolean;
	isTight?: boolean;
	isFlush?: boolean;
} & LinkProps;

const ButtonInner = (props: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
	const {
		className,
		intent,
		size,
		align,
		isDestructive,
		isActive,
		isLoading,
		children,
		leftIcon: LeftIcon,
		rightIcon: RightIcon,
		isDisabled,
		isTight,
		isFlush,
		href,
		...rest
	} = props;

	if (props.href) {
		const { isExternal } = props;
		return (
			<a
				className={classNames(
					buttonStyles({ intent, size, align, isActive, isTight, isFlush }),
					isDestructive ? buttonDestructiveVariants({ intent }) : "",
					className,
					isDisabled || isLoading ? "cursor-not-allowed opacity-50" : "",
				)}
				href={href}
				rel={isExternal ? "noopener noreferrer" : undefined}
				target={isExternal ? "_blank" : undefined}
				onClick={isDisabled || isLoading ? (e) => e.preventDefault() : undefined}
				aria-disabled={isDisabled || isLoading ? "true" : undefined}
			>
				{LeftIcon && <LeftIcon className="h-4 min-h-[16px] w-4 min-w-[16px] stroke-2" />}
				{isLoading && (
					<>
						<LoadingSpinner
							className={classNames("absolute", loadingSpinnerStyles({ intent, isDestructive }))}
						/>
						<span className="sr-only">Loading...</span>
					</>
				)}
				{/* Stable wrapper so React never removes text nodes re-parented by page translation */}
				<span className={classNames("contents", isLoading ? "invisible" : undefined)}>{children}</span>
				{isExternal && (
					<>
						{" "}
						<ExternalLinkIcon className="inline h-4 w-4" />
					</>
				)}
				{RightIcon && <RightIcon className="h-4 min-h-[16px] w-4 min-w-[16px] stroke-2" />}
			</a>
		);
	}

	return (
		<button
			ref={ref}
			{...rest}
			className={classNames(
				buttonStyles({ intent, size, align, isActive, isTight, isFlush }),
				isDestructive ? buttonDestructiveVariants({ intent }) : "",
				className,
			)}
			disabled={isDisabled || isLoading}
		>
			<>
				{LeftIcon && <LeftIcon className="h-4 min-h-[16px] w-4 min-w-[16px] stroke-2" />}
				{isLoading && (
					<>
						<LoadingSpinner
							className={classNames("absolute", loadingSpinnerStyles({ intent, isDestructive }))}
						/>
						<span className="sr-only">Loading...</span>
					</>
				)}
				{/* Stable wrapper so React never removes text nodes re-parented by page translation */}
				<span className={classNames("contents", isLoading ? "invisible" : undefined)}>{children}</span>
				{RightIcon && <RightIcon className="h-4 min-h-[16px] w-4 min-w-[16px] stroke-2" />}
			</>
		</button>
	);
};

ButtonInner.displayName = "Button";

export const Button = forwardRef(ButtonInner);
